import { ProxyWorkflowStatusType } from '../../core/models/proxy-workflow-status-type';

export type ProxyProgressStepState = 'complete' | 'current' | 'upcoming' | 'skipped' | 'failed';

export interface ProxyProgressStep {
  label: string;
  /** Second line under the label, e.g. why a step was skipped. */
  note: string | null;
  state: ProxyProgressStepState;
  /** 1-based position, shown in the marker for steps without a status glyph. */
  position: number;
}

const SUBMITTED = 'Submitted';
// Temporarily out of the stepper: no admin API sends the authorization email yet,
// so the stage could never legitimately complete. Restore it (and the entries
// below that reference it) when the send endpoint lands.
// const EMAIL_SENT = 'Auth Email Sent';
const PATIENT_CONFIRMED = 'Patient Confirmed';
const STAFF_REVIEW = 'Staff Review';
const ACTIVE = 'Active';
const DENIED = 'Denied';

function toSteps(
  entries: { label: string; note?: string | null; state: ProxyProgressStepState }[],
): ProxyProgressStep[] {
  return entries.map((entry, index) => ({
    label: entry.label,
    note: entry.note ?? null,
    state: entry.state,
    position: index + 1,
  }));
}

/**
 * Build the Request Progress stepper for a caregiver/proxy request.
 *
 * Stages are Submitted → Patient Confirmed → Staff Review → Active, with the
 * terminal stage becoming **Denied** for a denied request. (The "Auth Email Sent"
 * stage is commented out until the admin API can actually send that email.)
 *
 * Two shapes, because the proxy workflow genuinely has two:
 *
 * - **Standard** (4 stages) as above.
 * - **Minor** (3 stages) — the patient is verified through their guardian, so
 *   there is no patient confirmation to wait on.
 *
 * A denial can happen from any stage and the API only reports the *current*
 * status, so the stages between Submitted and the terminal one stay `upcoming`:
 * we genuinely don't know how far the request travelled before it was denied,
 * and marking them complete would invent history.
 *
 * An unrecognized status likewise leaves everything after Submitted `upcoming`,
 * which reads as "we don't know where this is" rather than inventing progress.
 */
export function buildProxyRequestProgress(
  workflowStatusTypeId: number,
  isMinor: boolean,
): ProxyProgressStep[] {
  const isApproved = workflowStatusTypeId === ProxyWorkflowStatusType.Approved;
  const isDenied = workflowStatusTypeId === ProxyWorkflowStatusType.Denied;

  // The last stage is Active or Denied — never both.
  const terminal = isDenied
    ? { label: DENIED, state: 'failed' as const }
    : { label: ACTIVE, state: isApproved ? ('complete' as const) : ('upcoming' as const) };

  if (isMinor) {
    // Pending / NeedsInfo shouldn't occur for a minor (there is no email step to
    // wait on), so anything short of Approved or Denied awaits staff review.
    const staffReviewState = isApproved ? 'complete' : isDenied ? 'upcoming' : 'current';

    return toSteps([
      { label: SUBMITTED, state: 'complete' },
      // { label: EMAIL_SENT, note: 'Skipped — Minor', state: 'skipped' },
      { label: STAFF_REVIEW, state: staffReviewState },
      terminal,
    ]);
  }

  // How many of the two stages between Submitted and the terminal one are done.
  const completedMiddleStages = ((): number => {
    switch (workflowStatusTypeId) {
      case ProxyWorkflowStatusType.Pending:
        return 0; // patient has not confirmed
      case ProxyWorkflowStatusType.NeedsInfo:
        return 0; // awaiting the patient's confirmation
      case ProxyWorkflowStatusType.InReview:
        return 1; // patient confirmed, sitting with staff
      case ProxyWorkflowStatusType.Approved:
        return 2;
      default:
        return 0; // denied or unrecognized — position unknown
    }
  })();

  const claimsProgress = !isDenied && workflowStatusTypeId in ProxyWorkflowStatusType;
  const middleStages = [PATIENT_CONFIRMED, STAFF_REVIEW];

  return toSteps([
    { label: SUBMITTED, state: 'complete' },
    ...middleStages.map((label, index) => {
      if (index < completedMiddleStages) {
        return { label, state: 'complete' as const };
      }
      // The first not-yet-done stage is where the request is sitting — unless the
      // status is denied or unrecognized, in which case claim nothing.
      const isNext = index === completedMiddleStages;
      return {
        label,
        state: isNext && claimsProgress ? ('current' as const) : ('upcoming' as const),
      };
    }),
    terminal,
  ]);
}
